import { DEAL_LISTING_TYPE, SERVICE_LISTING_TYPE, STAFF_LISTING_TYPE } from '../../util/types';

const CHILD_SERVICE_REQUEST_PAYMENT = 'transition/child-service-request-payment';

const normalizeListingId = id => {
  if (!id) {
    return null;
  }
  return typeof id === 'string' ? id : id?.uuid || null;
};

/**
 * Booked service or deal listing id stored on the transaction (protectedData).
 * Example serviceId: 6a1d7c3c-3e9d-40d1-94c9-6639cc94464f
 */
export const getBookedListingIdFromTransaction = tx => {
  if (!tx) {
    return null;
  }

  const protectedData = tx?.attributes?.protectedData || {};
  const listingPublicData = tx?.listing?.attributes?.publicData || {};
  const staffListingId = tx?.listing?.id?.uuid;

  const serviceId = normalizeListingId(
    protectedData.serviceId ?? protectedData.service_id ?? listingPublicData.serviceId
  );
  const dealId = normalizeListingId(
    protectedData.dealId ?? protectedData.deal_id ?? listingPublicData.dealId
  );

  // Prefer the id that is not the staff listing (staff bookings attach staff as tx.listing).
  if (serviceId && serviceId !== staffListingId) {
    return serviceId;
  }
  if (dealId && dealId !== staffListingId) {
    return dealId;
  }

  // Deal/service purchase: transaction listing may be the bookable listing.
  const listingType = listingPublicData?.listingType;
  if (listingType !== STAFF_LISTING_TYPE && staffListingId) {
    return staffListingId;
  }

  return serviceId || dealId || null;
};

/** @deprecated Use getBookedListingIdFromTransaction */
export const getServiceIdFromTransaction = getBookedListingIdFromTransaction;

export const resolveBookedListingId = getBookedListingIdFromTransaction;

/** @deprecated Use resolveBookedListingId */
export const resolveServiceId = resolveBookedListingId;

export const isStaffBookingTransaction = (tx, bookedListingId) => {
  const txListing = tx?.listing;
  const listingType = txListing?.attributes?.publicData?.listingType;
  if (listingType === STAFF_LISTING_TYPE) {
    return true;
  }
  const txListingId = txListing?.id?.uuid;
  return Boolean(bookedListingId && txListingId && txListingId !== bookedListingId);
};

const VARIANT_PRIORITY = [
  'listing-card',
  'listing-card-2x',
  'default',
  'scaled-medium',
  'scaled-small',
  'square-small',
];

const imageUrlFromVariants = variants => {
  if (!variants || typeof variants !== 'object') {
    return null;
  }
  for (const key of VARIANT_PRIORITY) {
    const url = variants[key]?.url;
    if (typeof url === 'string' && url.trim()) {
      return url;
    }
  }
  const any = Object.values(variants).find(entry => entry?.url);
  return any?.url || null;
};

const imageUrlFromImageEntity = image => {
  if (!image) {
    return null;
  }
  const directUrl = image?.url || image?.attributes?.url || image?.attributes?.originalUrl;
  if (typeof directUrl === 'string' && directUrl.trim()) {
    return directUrl;
  }
  return imageUrlFromVariants(image?.attributes?.variants);
};

/** Extract image URL from a listings.show API payload (listing + included). */
const normalizeEntityId = id => {
  if (!id) {
    return null;
  }
  if (typeof id === 'string') {
    return id;
  }
  return id?.uuid || String(id);
};

export const getImageUrlFromListingResponse = (listing, included = []) => {
  const imageRefs = listing?.relationships?.images?.data;
  if (Array.isArray(imageRefs) && imageRefs.length > 0) {
    const firstImageRefId = normalizeEntityId(imageRefs[0]?.id);
    const firstImage = included.find(
      item =>
        item?.type === 'image' && normalizeEntityId(item?.id) === firstImageRefId
    );
    const url = imageUrlFromImageEntity(firstImage);
    if (url) {
      return url;
    }
  }

  return imageUrlFromImageEntity(listing?.images?.[0]);
};

export const getCheckoutListingImageUrl = checkoutListing => {
  return imageUrlFromImageEntity(checkoutListing?.images?.[0]);
};

/** Image URL saved on cart line items during booking (plain string). */
export const getCheckoutCartImageUrl = checkoutData => {
  const cart = checkoutData?.orderData?.cart;
  if (!cart || typeof cart !== 'object') {
    return null;
  }
  for (const merchantCart of Object.values(cart)) {
    if (!merchantCart || typeof merchantCart !== 'object') {
      continue;
    }
    for (const item of Object.values(merchantCart)) {
      if (typeof item?.image === 'string' && item.image.trim()) {
        return item.image.trim();
      }
    }
  }
  return null;
};

export const getListingImageUrl = listing => {
  if (!listing) {
    return null;
  }
  if (Array.isArray(listing.images)) {
    for (const image of listing.images) {
      const url = imageUrlFromImageEntity(image);
      if (url) {
        return url;
      }
    }
  }
  return null;
};

const getBookingStartMsFromCheckoutData = checkoutData => {
  const cart = checkoutData?.orderData?.cart;
  if (!cart || typeof cart !== 'object') {
    return null;
  }

  for (const merchantCart of Object.values(cart)) {
    if (!merchantCart || typeof merchantCart !== 'object') {
      continue;
    }
    for (const item of Object.values(merchantCart)) {
      const dealStart = item?.dealListingIds?.[0]?.bookingStart;
      if (dealStart != null && dealStart !== '') {
        return Number(dealStart);
      }
      if (item?.bookingStart != null && item?.bookingStart !== '') {
        return Number(item.bookingStart);
      }
    }
  }

  return null;
};

const getBookingEndMsFromCheckoutData = checkoutData => {
  const cart = checkoutData?.orderData?.cart;
  if (!cart || typeof cart !== 'object') {
    return null;
  }

  for (const merchantCart of Object.values(cart)) {
    if (!merchantCart || typeof merchantCart !== 'object') {
      continue;
    }
    for (const item of Object.values(merchantCart)) {
      const dealEnd = item?.dealListingIds?.[0]?.bookingEnd;
      if (dealEnd != null && dealEnd !== '') {
        return Number(dealEnd);
      }
      if (item?.bookingEnd != null && item?.bookingEnd !== '') {
        return Number(item.bookingEnd);
      }
    }
  }

  return null;
};

/**
 * Deal bookings attach staff + slot on a child service transaction
 * (protectedData.dealChildTransactions), not the parent deal tx.
 */
export const resolveDealChildServiceTransaction = (parentTx, childTransactions = []) => {
  const dealChildIds = parentTx?.attributes?.protectedData?.dealChildTransactions || [];
  if (!dealChildIds.length || !Array.isArray(childTransactions) || childTransactions.length === 0) {
    return null;
  }

  const normalizedChildIds = new Set(
    dealChildIds.map(id => normalizeListingId(id)).filter(Boolean)
  );

  const isReferencedChild = tx => normalizedChildIds.has(tx?.id?.uuid);

  const serviceChild = childTransactions.find(tx => {
    if (!isReferencedChild(tx)) {
      return false;
    }
    const hasServiceId = Boolean(tx?.attributes?.protectedData?.serviceId);
    const isServiceChild = (tx?.attributes?.transitions || []).some(
      t => t.transition === CHILD_SERVICE_REQUEST_PAYMENT
    );
    return hasServiceId || isServiceChild;
  });
  if (serviceChild) {
    return serviceChild;
  }

  return childTransactions.find(tx => isReferencedChild(tx) && tx?.booking) || null;
};

/** Booking slot start from child/parent tx.booking or checkout cart (never order createdAt). */
export const resolveBookingStart = (tx, checkoutData, bookingTx) => {
  const effectiveTx = bookingTx || tx;
  const bookingAttrs = effectiveTx?.booking?.attributes || {};
  const start = bookingAttrs.displayStart || bookingAttrs.start;
  if (start) {
    return new Date(start);
  }

  const cartStartMs = getBookingStartMsFromCheckoutData(checkoutData);
  if (cartStartMs && !Number.isNaN(cartStartMs)) {
    return new Date(cartStartMs);
  }

  return null;
};

/** Booking slot end from child/parent tx.booking or checkout cart (never order createdAt). */
export const resolveBookingEnd = (tx, checkoutData, bookingTx) => {
  const effectiveTx = bookingTx || tx;
  const bookingAttrs = effectiveTx?.booking?.attributes || {};
  const end = bookingAttrs.displayEnd || bookingAttrs.end;
  if (end) {
    return new Date(end);
  }

  const cartEndMs = getBookingEndMsFromCheckoutData(checkoutData);
  if (cartEndMs && !Number.isNaN(cartEndMs)) {
    return new Date(cartEndMs);
  }

  return null;
};

/** Algolia listingType param for the "More deals" / "More services" CTA. */
export const resolveBookedListingType = (tx, bookedListing, checkoutData) => {
  const bookedType = bookedListing?.attributes?.publicData?.listingType;
  if (bookedType === DEAL_LISTING_TYPE) {
    return 'deals';
  }
  if (bookedType === SERVICE_LISTING_TYPE) {
    return 'services';
  }

  const protectedData = tx?.attributes?.protectedData || {};
  if (protectedData.dealId || protectedData.deal_id) {
    return 'deals';
  }
  if (protectedData.serviceId || protectedData.service_id) {
    return 'services';
  }

  const processName = tx?.attributes?.processName || '';
  if (processName.includes('deal')) {
    return 'deals';
  }

  const cart = checkoutData?.orderData?.cart;
  if (cart && typeof cart === 'object') {
    for (const merchantCart of Object.values(cart)) {
      if (!merchantCart || typeof merchantCart !== 'object') {
        continue;
      }
      for (const item of Object.values(merchantCart)) {
        if (item?.type === DEAL_LISTING_TYPE) {
          return 'deals';
        }
        if (item?.type === SERVICE_LISTING_TYPE) {
          return 'services';
        }
      }
    }
  }

  return 'deals';
};

const isEmailLike = value =>
  typeof value === 'string' && value.includes('@') && value.includes('.');

/** Prefer a human-readable staff label over raw listing title (often an email). */
export const getStaffDisplayName = staffListing => {
  if (!staffListing) {
    return null;
  }
  const attributes = staffListing?.attributes || {};
  const publicData = attributes?.publicData || {};
  const title = (attributes?.title || '').trim();

  const named =
    publicData.name || publicData.displayName || publicData.fullName || publicData.staffName;
  if (typeof named === 'string' && named.trim()) {
    return named.trim();
  }

  if (title && !isEmailLike(title)) {
    return title;
  }

  if (isEmailLike(title)) {
    const localPart = title.split('@')[0] || '';
    const readable = localPart.replace(/[._-]+/g, ' ').trim();
    return readable || title;
  }

  return title || null;
};

export const getListingPlace = listing => {
  if (!listing) {
    return '';
  }
  const locationRaw = listing?.attributes?.publicData?.location;
  return (
    locationRaw?.selectedPlace?.address ||
    locationRaw?.address ||
    (typeof locationRaw === 'string' ? locationRaw : '')
  );
};

/**
 * Resolve service/deal title and image for the summary header (never staff).
 */
export const resolveServiceDisplay = (
  tx,
  bookedListing,
  checkoutListing,
  bookedListingPreview,
  checkoutData,
  bookingTx
) => {
  const txListing = tx?.listing || {};
  const bookedListingId = resolveBookedListingId(tx);
  const isStaffTx = isStaffBookingTransaction(tx, bookedListingId);
  const staffListing = bookingTx?.listing || (isStaffTx ? txListing : null);
  // Negotiated-flow bookings keep the SERVICE listing as tx.listing (the staff
  // reservation lives on a separate, linked transaction), so the staff name is
  // denormalized directly onto protectedData at booking time instead.
  const staffName =
    getStaffDisplayName(staffListing) || tx?.attributes?.protectedData?.selectedStaffName || null;

  const headerTitle =
    bookedListing?.attributes?.title ||
    bookedListingPreview?.title ||
    checkoutListing?.attributes?.title ||
    (!isStaffTx ? txListing?.attributes?.title : '') ||
    '';

  const headerImageUrl =
    bookedListingPreview?.imageUrl ||
    getListingImageUrl(bookedListing) ||
    getCheckoutListingImageUrl(checkoutListing) ||
    getCheckoutCartImageUrl(checkoutData) ||
    null;

  const listingPlace = getListingPlace(
    bookedListing || checkoutListing || (!isStaffTx ? txListing : null)
  );

  return {
    bookedListingId,
    isStaffTx,
    headerTitle,
    headerImageUrl,
    staffName,
    listingPlace,
    bookedListing,
  };
};
